<script setup>
import { useRoute } from "vue-router";
import _cloneDeep from "lodash/cloneDeep";
import { useI18n } from "vue-i18n";
import AutopilotModule from "@Pages/Projects/Shared/Modules/AutopilotModule.vue";
import { PrimaryButton } from "@Components/Button";
import { useForm } from "@/form";
import TitleAndLanguage from "../Shared/TitleAndLanguage.vue";
import ActionMessage from "@Components/ActionMessage.vue";
import Breadcrumb from "@Components/Breadcrumb.vue";
import SettingsSidebar from "../Shared/SettingsSidebar.vue";
import UseTemplateModal from "@Components/Templates/UseTemplateModal.vue";
import _merge from "lodash/merge";
import { ref } from "vue";
import ClassicContentModule from "../Shared/Modules/ClassicContentModule.vue";
import CustomContentModule from "../Shared/Modules/CustomContentModule.vue";

const { t: __ } = useI18n();
const route = useRoute();
const useTemplateModal = ref();
const [project, personas, users, templates, statuses, categories, postTypes] = await Promise.all([
    new Promise((resolve, reject) => {
        useForm({ id: route.params.id })
            .post("projects_show")
            .then((response) => {
                resolve(response);
            })
            .catch((response) => {
                reject();
            });
    }),
    new Promise((resolve, reject) => {
        useForm()
            .post("personas_index")
            .then((response) => {
                resolve(response);
            })
            .catch((response) => {
                reject();
            });
    }),
    new Promise((resolve, reject) => {
        useForm()
            .post("wordpress_users")
            .then((response) => {
                resolve(response);
            })
            .catch((response) => {
                reject();
            });
    }),
    new Promise((resolve, reject) => {
        useForm()
            .post("wordpress_templates")
            .then((response) => {
                resolve(response);
            })
            .catch((response) => {
                reject();
            });
    }),
    new Promise((resolve, reject) => {
        useForm()
            .post("wordpress_statuses")
            .then((response) => {
                resolve(response);
            })
            .catch((response) => {
                reject();
            });
    }),
    new Promise((resolve, reject) => {
        useForm()
            .post("wordpress_categories")
            .then((response) => {
                resolve(response);
            })
            .catch((response) => {
                reject();
            });
    }),
    new Promise((resolve, reject) => {
        useForm()
            .post("wordpress_post_types")
            .then((response) => {
                resolve(response);
            })
            .catch((response) => {
                reject();
            });
    }),
]);

const form = useForm(_cloneDeep(project));
if (form.language) {
    form.language = form.language.key;
}

const submit = async (key) => {
    if (form.processing) return;

    form.post("projects_update");
};

const useTemplate = (template) => {
    // empty all array settings
    form.modules.content.blocks = [];
    form.modules.content.automatic.custom_links.links = [];
    form.modules.content.automatic.keywords.custom = [];
    form.modules.wordpress.categories.custom = [];
    form.modules.wordpress.tags.custom = [];
    form.modules.wordpress.custom_fields = [];

    form.modules = _merge(form.modules, _cloneDeep(template.payload.modules));
    form.persona_id = template.payload.persona_id;

    useTemplateModal.value.hide();
};
</script>

<template>
    <div>
        <Breadcrumb
            :pages="[
                { name: __('Mes projets'), href: { name: 'projects.index' } },
                { name: __('Projet #{id}', { id: project.id }), href: { name: 'publications.index', params: { id: project.id } } },
                { name: __('Modification du projet #{id}', { id: project.id }) },
            ]" />
        <TitleAndLanguage v-model="form" />

        <div class="mx-auto flex flex-col mt-6 space-y-6">
            <div class="flex justify-end space-x-3 lg:px-8">
                <PrimaryButton @click.prevent="$refs.useTemplateModal.show()">{{ __("Utiliser un template") }}</PrimaryButton>
                <UseTemplateModal ref="useTemplateModal" @use="useTemplate" />
                <PrimaryButton v-if="form.modules.content.automatic.enabled" @click="form.modules.content.automatic.enabled = false">{{
                    __("Mode avancé")
                }}</PrimaryButton>
                <PrimaryButton v-else @click="form.modules.content.automatic.enabled = true">{{ __("Mode classique") }}</PrimaryButton>
            </div>

            <AutopilotModule
                :modelValue="form"
                @update:modelValue="$emit('update:modelValue', $event)"
                v-if="form.id !== undefined && (form.type === 'news' || form.type === 'rss')" />

            <ClassicContentModule
                v-if="form.modules.content.automatic.enabled"
                :modelValue="form"
                @update:modelValue="$emit('update:modelValue', $event)"
                :personas="personas" />

            <CustomContentModule v-else :modelValue="form" @update:modelValue="$emit('update:modelValue', $event)" :personas="personas" />

            <SettingsSidebar
                :templates="templates"
                :statuses="statuses"
                :categories="categories"
                :postTypes="postTypes"
                :users="users"
                :modelValue="form"
                @update:modelValue="$emit('update:modelValue', $event)" />
        </div>

        <div class="mx-auto flex justify-end items-center p-6 lg:px-8">
            <ActionMessage :on="form.recentlySuccessful" class="mr-3"> {{ __("Sauvegardé.") }} </ActionMessage>

            <PrimaryButton @click="submit" :processing="form.processing"> {{ __("Sauvegarder") }} </PrimaryButton>
        </div>
    </div>
</template>
